import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useState, useEffect } from "react";

import { jsPDF } from "jspdf";
import { Download } from "lucide-react";
import { useGetAllFeaturePlans } from "@/api/plan/get-all-feature-plans";
import { useCreateSubscription } from "@/api/billing/create-subscription";
import { useCancelSubscription } from "@/api/billing/cancel-subscription";
import { useGetMySubscription } from "@/api/billing/get-my-subscription";
import { useGetBillingHistory } from "@/api/billing/get-billing-history";
import useOnboardingData from "@/store/onboarding-data";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type FeaturePlan = {
    plan_id: number | string;
    plan_name: string;
    is_active: boolean;
    plan_created_at: string | number;
    plan_price: number | string;
    plan_description: string;
    plans_interval?: string;
    features?: {
        id: number;
        feature_id: number;
        feature_name: string;
        feature_type: string;
        feature_description: string | null;
        value: string;
    }[];
};

// Define the type for a billing history item
interface BillingHistoryItem {
    invoice_id: string;
    invoice_number: string;
    description: string;
    amount: number;
    currency: string;
    status: string;
    due_date: string;
    paid_at: string | null;
    payment_status: string;
    payment_method: string;
    created_at: string;
    payment_date: string;
}

const Subscriptions = () => {
    const { data: featurePlansData, isLoading, error } = useGetAllFeaturePlans();
    const { storeData } = useOnboardingData();
    const { data: mySubscriptionData, isLoading: mySubscriptionLoading } = useGetMySubscription(storeData?.id || "");
    const { data: billingHistoryData, isLoading: billingHistoryLoading } = useGetBillingHistory(storeData?.id || "");

    const plans = featurePlansData?.data || [];
    const createSubscriptionMutation = useCreateSubscription();
    const cancelSubscriptionMutation = useCancelSubscription();
    const [activePlanId, setActivePlanId] = useState<string | null>(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [pendingCancellation, setPendingCancellation] = useState<{ store_id: string } | null>(null);

    // Set the active plan based on user's current subscription
    useEffect(() => {
        if (mySubscriptionData) {
            if (!mySubscriptionData.meta.error && mySubscriptionData.data && mySubscriptionData.data.is_active) {
                setActivePlanId(String(mySubscriptionData.data.plan_id));
            } else {
                setActivePlanId(null);
            }
        }
    }, [mySubscriptionData]);

    // Handle the actual cancellation
    const handleCancelSubscription = () => {
        if (pendingCancellation) {
            cancelSubscriptionMutation.mutate(pendingCancellation);
            setPendingCancellation(null);
        }
        setShowCancelDialog(false);
    };

    const handleDownloadInvoice = (invoice: BillingHistoryItem) => {
        const doc = new jsPDF();

        // --- Invoice Header ---
        doc.setFontSize(22);
        doc.setFont("helvetica", "bold");
        doc.text("INVOICE", 105, 20, { align: "center" });

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Golden Info Systems Ltd.", 20, 30);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("House #13, Gareeb-E-Newaz Avenue, Sector 13, Uttara, Dhaka", 20, 35);

        doc.text(`Invoice #: ${invoice.invoice_id}`, 190, 30, { align: "right" });
        doc.text(`Date: ${new Date(invoice.created_at).toLocaleDateString()}`, 190, 35, { align: "right" });

        // --- Bill To Section ---
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Bill To:", 20, 60);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.text("Customer Name", 20, 65);
        doc.text("Customer Address", 20, 70);

        // --- Items Table ---
        const tableColumn = ["#", "Description", "Quantity", "Unit Price", "Total"];
        const tableRows: (string | number)[][] = [];

        const itemData = [1, invoice.description, 1, `$${invoice.amount.toFixed(2)}`, `$${invoice.amount.toFixed(2)}`];
        tableRows.push(itemData);

        (doc as any).autoTable({
            startY: 85,
            head: [tableColumn],
            body: tableRows,
            theme: "striped",
            headStyles: { fillColor: [62, 82, 182] },
            margin: { top: 80 },
        });

        // --- Total Amount ---
        const finalY = (doc as any).lastAutoTable.finalY || 150;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Total Amount:", 140, finalY + 15);
        doc.text(`$${invoice.amount.toFixed(2)}`, 190, finalY + 15, { align: "right" });

        // --- Footer ---
        doc.setFontSize(8);
        doc.text("Thank you for your business!", 105, doc.internal.pageSize.height - 10, { align: "center" });

        // --- Save the PDF ---
        const invoiceFileName = `Invoice_${invoice.invoice_number.replace(/\s+/g, "_")}.pdf`;
        doc.save(invoiceFileName);
    };

    return (
        <div className="bg-white mt-4 rounded-lg p-4 sm:p-6 lg:p-8 font-sans flex justify-center items-start">
            <div className="w-full space-y-8">
                {/* Plan Section */}
                <div className="text-2xl font-semibold text-gray-800 mb-6">Plan</div>
                {isLoading || mySubscriptionLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="text-lg text-gray-600">Loading plans...</div>
                    </div>
                ) : error ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="text-lg text-red-600">Error loading plans. Please try again later.</div>
                    </div>
                ) : plans.length === 0 ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="text-lg text-gray-600">No plans available at the moment.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {plans.map((plan: FeaturePlan) => {
                            const isActive = activePlanId !== null && Number(activePlanId) === Number(plan.plan_id);

                            // Determine background class based on position
                            const getBackgroundClass = () => {
                                const planIndex = plans.findIndex((p: FeaturePlan) => String(p.plan_id) === String(plan.plan_id));
                                if (planIndex === 0) return "bg-gradient-to-br from-blue-500 to-teal-500 text-white";
                                if (planIndex === 1) return "bg-gradient-to-br from-purple-600 to-indigo-700 text-white";
                                if (planIndex === 2) return "bg-gradient-to-br from-amber-500 to-orange-500 text-white";
                                return "bg-gradient-to-br from-slate-600 to-gray-700 text-white";
                            };

                            const planIndex = plans.findIndex((p: FeaturePlan) => String(p.plan_id) === String(plan.plan_id));
                            const isGradientCard = !isActive;

                            return (
                                <Card
                                    key={plan.plan_id}
                                    className={`rounded-lg p-4 shadow-md transition-all duration-300 h-full ${
                                        isActive
                                            ? "border-2 border-purple-500 ring-1 ring-purple-200"
                                            : "border border-gray-200 " + getBackgroundClass()
                                    } relative overflow-hidden`}
                                >
                                    {isActive && (
                                        <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-bl-md">
                                            ACTIVE
                                        </div>
                                    )}

                                    <div className="text-center mb-3">
                                        <CardTitle className={`text-lg font-bold ${isGradientCard ? "text-white" : "text-gray-900"}`}>
                                            {plan.plan_name}
                                        </CardTitle>
                                        <p className={`text-xs mt-1 ${isGradientCard ? "text-white/80" : "text-gray-600"}`}>
                                            {plan.plan_description}
                                        </p>
                                    </div>

                                    <div className={`text-center text-xl font-bold mb-3 ${isGradientCard ? "text-white" : "text-gray-900"}`}>
                                        ${typeof plan.plan_price === 'number' ? plan.plan_price : parseFloat(plan.plan_price)}
                                        <span className="text-sm font-normal">/month</span>
                                    </div>

                                    <div className="space-y-2 mb-4 max-h-40 overflow-y-auto pr-1">
                                        {plan.features && plan.features.length > 0 ? (
                                            <ul className="space-y-1">
                                                {plan.features.map((feature: any) => (
                                                    <li
                                                        key={feature.id}
                                                        className={`flex justify-between text-xs px-2 py-1 ${
                                                            isGradientCard ? "text-white/90" : "text-gray-600"
                                                        }`}
                                                    >
                                                        <span>{feature.feature_name}:</span>
                                                        <span className="font-medium">{feature.value}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className={`${isGradientCard ? "text-white/90" : "text-gray-600"} text-xs text-center py-4`}>
                                                No features available
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-auto pt-2">
                                        <Button
                                            variant={isGradientCard ? "default" : "outline"}
                                            className={`w-full py-1.5 text-sm ${
                                                isGradientCard
                                                    ? "bg-white text-purple-700 hover:bg-gray-100"
                                                    : "text-blue-600 border border-blue-500 hover:bg-blue-50"
                                            }`}
                                            onClick={() => {
                                                if (isActive) {
                                                    // Show confirmation dialog before canceling
                                                    setShowCancelDialog(true);
                                                    setPendingCancellation({ store_id: storeData?.id || "" });
                                                } else {
                                                    // If this is not the active plan, create a new subscription
                                                    if (storeData?.id) {
                                                        createSubscriptionMutation.mutate({
                                                            store_id: storeData.id,
                                                            plan_id: String(plan.plan_id),
                                                        });
                                                    } else {
                                                        console.error("No store selected for subscription");
                                                    }
                                                }
                                            }}
                                            disabled={createSubscriptionMutation.isPending || (isActive && cancelSubscriptionMutation.isPending)}
                                        >
                                            {isActive && cancelSubscriptionMutation.isPending
                                                ? "Canceling..."
                                                : createSubscriptionMutation.isPending &&
                                                  String(createSubscriptionMutation.variables?.plan_id) === String(plan.plan_id)
                                                ? "Processing..."
                                                : isActive
                                                ? "Cancel"
                                                : planIndex === 0
                                                ? "Get It Now"
                                                : "Buy Now"}
                                        </Button>
                                    </div>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Auto Renew Section */}
                {/* <div className="flex items-center justify-between space-x-2 mt-8 p-4 bg-white rounded-xl shadow-sm">
                    <div className="flex flex-col">
                        <Label htmlFor="auto-renew" className="text-lg font-medium text-gray-800">
                            Enable auto renew
                        </Label>
                        <CardDescription className="text-gray-500 mt-1">
                            This option, if checked, will renew your productive subscription if the current plan expires. However, this might prevent
                            you from...
                        </CardDescription>
                    </div>
                    <Switch id="auto-renew" checked={autoRenew} onCheckedChange={setAutoRenew} className="data-[state=checked]:bg-blue-500" />
                </div> */}

                {/* Payment Method Section */}
                {/* <div className="text-2xl font-semibold text-gray-800 mt-8 mb-6">Payment Method</div> */}
                {/* <RadioGroup
                    defaultValue="creditCard"
                    value={paymentMethod}
                    onValueChange={(value: "creditCard" | "debitCard") => setPaymentMethod(value)}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                > */}
                {/* Credit Card Option */}
                {/* <Card
                        className={`rounded-xl p-4 flex flex-col items-center justify-center shadow-lg cursor-pointer transition-all duration-300 ${
                            paymentMethod === "creditCard" ? "border-blue-500 ring-2 ring-blue-500" : "border-gray-200"
                        }`}
                    >
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center space-x-3">
                                <img src="https://placehold.co/40x25/f8f8f8/000000?text=💳" alt="Credit Card" className="h-6 w-auto" />
                                <span className="text-gray-700 font-medium">Credit Card</span>
                            </div>
                            {paymentMethod === "creditCard" && (
                                <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            )}
                        </div>
                        <div className="text-gray-600 text-sm mt-3">**** **** **** 3542</div>
                    </Card> */}

                {/* Debit Card Option */}
                {/* <Card
                        className={`rounded-xl p-4 flex flex-col items-center justify-center shadow-lg cursor-pointer transition-all duration-300 ${
                            paymentMethod === "debitCard" ? "border-blue-500 ring-2 ring-blue-500" : "border-gray-200"
                        }`}
                    >
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center space-x-3">
                                <img src="https://placehold.co/40x25/f8f8f8/000000?text=💳" alt="Debit Card" className="h-6 w-auto" />
                                <span className="text-gray-700 font-medium">Debit Card</span>
                            </div>
                            {paymentMethod === "debitCard" && (
                                <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            )}
                        </div>
                        <div className="text-gray-600 text-sm mt-3">**** **** **** 1543</div>
                    </Card> */}

                {/* Add New Payment Method */}
                {/* <Card className="rounded-xl p-4 flex flex-col items-center justify-center shadow-lg border-dashed border-gray-300 text-gray-500 cursor-pointer hover:border-gray-400 transition-colors duration-200">
                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-100 text-gray-600">
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                        </div>
                        <div className="mt-2 text-sm font-medium">Add new method</div>
                    </Card> */}
                {/* </RadioGroup> */}

                {/* Billing History Section */}
                <div className="text-2xl font-semibold text-gray-800 mt-8 mb-6">Billing History</div>
                <Card className="rounded-xl shadow-lg overflow-hidden">
                    <CardContent className="p-0">
                        {billingHistoryLoading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="text-lg text-gray-600">Loading billing history...</div>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader className="bg-gray-100">
                                    <TableRow>
                                        <TableHead className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </TableHead>
                                        <TableHead className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Details
                                        </TableHead>
                                        <TableHead className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </TableHead>
                                        <TableHead className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Download
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="divide-y divide-gray-200">
                                    {billingHistoryData?.data && billingHistoryData.data.length > 0 ? (
                                        billingHistoryData.data.map((invoice) => (
                                            <TableRow key={invoice.invoice_id} className="hover:bg-gray-50 transition-colors">
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {new Date(invoice.created_at).toLocaleDateString()}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {invoice.description}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    ${invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                                                </TableCell>
                                                <TableCell className="px-6 py-4 whitespace-nowrap text-sm " align="right">
                                                    <button
                                                        onClick={() => handleDownloadInvoice(invoice)}
                                                        className="flex items-center text-purple-600 hover:text-purple-800 hover:underline font-medium transition-colors"
                                                        aria-label={`Download invoice ${invoice.invoice_number}`}
                                                    >
                                                        <Download size={16} className="mr-1.5" />
                                                        Invoice
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} className="px-6 py-10 text-center text-sm text-gray-500">
                                                No billing history found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>

                {/* Cancel Subscription Confirmation Dialog */}
                <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This action cannot be undone. Cancelling your subscription will:
                                <ul className="list-disc pl-5 mt-2 space-y-1">
                                    <li>Immediately stop billing for this plan</li>
                                    <li>Remove access to premium features at the end of the billing period</li>
                                    <li>Not refund any unused portion of your subscription</li>
                                </ul>
                                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                    <strong className="text-yellow-800">Note:</strong> Your account will remain active until the end of the current
                                    billing period.
                                </div>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                            <AlertDialogAction onClick={handleCancelSubscription} className="bg-red-600 hover:bg-red-700">
                                Yes, Cancel Subscription
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </div>
    );
};

export default Subscriptions;
